"use client";
import { useEffect, useMemo, useState } from "react";
import { cn, formatNumberWithDots } from "@/lib/utils";
import {
  IDefaultPackage,
  IPackages,
  IUserPackage,
  servicesCategoriesKeys,
  TServiceNavigationItem,
} from "@/interfaces";
import { useSession } from "next-auth/react";
import { CustomButton } from "@/components/ui";
import { BdAltArrowLeft, BdAltArrowRight } from "solar-icon-react/bd";
import { usePackages } from "@/context/packagesContext";
import { getClientServiceKeyByAPIFlag } from "@/lib/adapters/serviceFlagAdapter";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { coreClient } from "@/services";
import { getPackagesAdapter } from "@/lib/adapters/getPackagesAdapter";
import UserPackageRadioGroup from "../userPackageRadioGroup";
import RadioPackageGroup from "@/components/packagesRadioGroup";

interface Props {
  selectedService?: TServiceNavigationItem;
  handleBackBtn?: () => void;
  pageData?: string;
  packages?: IPackages[];
  onPackageSelect?: (packageId: string) => void;
  handleUserPackageSelect: (pkg: IDefaultPackage | IUserPackage) => void;
  handlePackageSelect: (pkg: IPackages | IDefaultPackage) => void;
  serviceId?: string | number;
  showData?: "category" | "service";
  allowedCategories?: servicesCategoriesKeys[];
  walletBalanceData: any;
  inquiryData: any;
  selectedPackage: IDefaultPackage | IPackages;
  onNextStep: () => void;
  isUserPackage: boolean;
  serviceManualCost?: number;
  hideAction?: boolean;
}

const FALLBACK_DEFAULT_PACKAGE: IDefaultPackage = {
  id: "p0",
  name: "استعلام",
  price: "0",
};

export default function PaymentModalStepOne({
  selectedService,
  packages,
  onPackageSelect,
  showData = "category",
  serviceId,
  allowedCategories,
  handleBackBtn,
  pageData,
  walletBalanceData,
  inquiryData,
  handleUserPackageSelect,
  handlePackageSelect,
  selectedPackage,
  onNextStep,
  isUserPackage = false,
  serviceManualCost,
  hideAction = false,
}: Props) {
  const { packages: globalPackages, user: globalUser, setUser } = usePackages();

  const { data: session } = useSession();

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isGlobalUserKnown = globalUser !== undefined && globalUser !== null;

  const walletBalance = Number(walletBalanceData?.data?.balance || 0);
  const serviceCost = Number(
    serviceManualCost || inquiryData?.data?.price_user || 0,
  );

  const isWalletBalanceInsufficient =
    walletBalance === 0 || walletBalance < serviceCost;

  // Local state to hold packages fetched by service flag (inquiryFlag)
  const [flagPackages, setFlagPackages] = useState<IPackages[] | null>(null);

  // Fetch packages for the specific service flag when modal opens
  useEffect(() => {
    const apiKey = selectedService?.apiKey as string | undefined;
    if (!apiKey) return;

    let isCancelled = false;
    (async () => {
      try {
        const [packagesRes, inquiriesRes] = await Promise.all([
          coreClient.getPackagesData({
            token: session?.user.token,
            inquiryFlag: apiKey,
          }),
          coreClient.getInquiries(),
        ]);
        const adapted = getPackagesAdapter(
          (packagesRes as any)?.data,
          inquiriesRes?.data,
        );
        if (!isCancelled) {
          setFlagPackages(Array.isArray(adapted.list) ? adapted.list : []);

          if (adapted.user !== undefined) {
            setUser(adapted.user);
          }
        }
      } catch (e) {
        if (!isCancelled) setFlagPackages([]);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [selectedService?.apiKey, session?.user.token, setUser]);

  const defaultPackage: IDefaultPackage = {
    id: "p0",
    name: inquiryData?.data?.name || FALLBACK_DEFAULT_PACKAGE.name,
    price: formatNumberWithDots(serviceCost) || String(serviceCost),
  };

  const allPackages: IPackages[] = useMemo(
    () => flagPackages ?? (packages || globalPackages || []),
    [flagPackages, packages, globalPackages],
  );

  // Filter by serviceId
  const filterByServiceId = (items: IPackages[]) => {
    if (!serviceId) return items;
    return items.filter(
      (p) =>
        Array.isArray(p.serviceIds) && p.serviceIds.includes(Number(serviceId)),
    );
  };

  // Filter by categories
  const filterByCategories = (items: IPackages[]) => {
    const categoriesToFilter =
      allowedCategories || (allowedCategory ? [allowedCategory] : null);

    if (!categoriesToFilter || categoriesToFilter.length === 0) return items;

    return items.filter(
      (p) =>
        Array.isArray(p.categories) &&
        p.categories.some((c) => categoriesToFilter.includes(c)),
    );
  };

  const allowedCategory: servicesCategoriesKeys | null = useMemo(() => {
    const apiKey = selectedService?.apiKey as string | undefined;
    if (!apiKey) return null;
    const enumKey = getClientServiceKeyByAPIFlag(apiKey);
    if (!enumKey) return null;
    return globalRawServicesData[enumKey]?.category ?? null;
  }, [selectedService?.apiKey]);

  const filteredPackages: IPackages[] = useMemo(() => {
    if (showData === "service") {
      return filterByServiceId(allPackages);
    }
    return filterByCategories(allPackages);
  }, [allPackages, showData, serviceId, allowedCategories, allowedCategory]);

  useEffect(() => {
    if (onPackageSelect && selectedPackage) {
      if ("campaign_id" in selectedPackage && selectedPackage.campaign_id) {
        onPackageSelect(selectedPackage.campaign_id);
      } else if (selectedPackage.id === "p0") {
        onPackageSelect("");
      }
    }
  }, [selectedPackage, onPackageSelect]);

  const onSubmit = () => {
    onNextStep();
  };

  return (
    <>
      {pageData === "military" && !hideAction && (
        <div className="flex items-center justify-start! pt-3">
          <CustomButton
            variant="text"
            icon={<BdAltArrowRight size={20} />}
            className="mx-0! w-fit! max-w-[280px] px-3! text-sm!"
            onClick={handleBackBtn}
          >
            بازگشت به انتخاب سرویس
          </CustomButton>
        </div>
      )}
      <div>
        {isMounted && (
          <>
            {isGlobalUserKnown &&
            Array.isArray(globalUser) &&
            globalUser.length > 0 ? (
              <div className={hideAction ? undefined : "mt-3 px-4 pt-3"}>
                <UserPackageRadioGroup
                  packages={globalUser}
                  selectedPackage={selectedPackage}
                  setSelectedPackage={handleUserPackageSelect}
                  walletBalance={walletBalance}
                />
              </div>
            ) : (
              walletBalanceData?.data?.payment_type !== "postpaid" &&
              filteredPackages.length > 0 && (
                <div
                  className={
                    hideAction ? undefined : "mt-3 mb-4 px-4 pt-3 sm:mb-6"
                  }
                >
                  <RadioPackageGroup
                    packages={filteredPackages}
                    defaultValue={defaultPackage}
                    selectedPackage={selectedPackage}
                    setSelectedPackage={handlePackageSelect}
                  />
                </div>
              )
            )}
          </>
        )}
      </div>
      {!hideAction && (
        <div className="my-10 flex justify-center">
          <CustomButton
            className={cn("w-full max-w-[190px] px-2!")}
            onClick={onSubmit}
            loadingLabel="inquiring"
            id="military-services-next-step"
            icon={!isUserPackage && <BdAltArrowLeft size={20} />}
            iconPosition="end"
          >
            {isUserPackage ? "استعلام" : "مرحله بعد"}
          </CustomButton>
        </div>
      )}
    </>
  );
}
